import { useEffect, useRef } from "react";
import { createPortal } from "react-dom";

/**
 * The lines drawn across the page between two cards that are linked: the open
 * payment and the suggestion picked for it, or a payment and what Similar's
 * line-up has beside it, or Similar's payments and the post picked for them all.
 * Each end is a ball on its card (``TriageLinkBall``, ``data-link-from`` /
 * ``data-link-to``, a link's ends sharing its name - several leaving from
 * where one comes to, as Similar's do);
 * a line is drawn between the two while both are engaged, in steps - over
 * both panels and the divider between them, read from where the balls are
 * each frame, so it follows the cards as they scroll and move - and none while
 * either end is under a card held over it, or the end it leaves from is
 * scrolled out of its list. One coming to a card scrolled out of its list - the
 * suggestion picked - runs on up or down to that list's edge and stops there. A ball under
 * a card held is not drawn either: half past the edge of the card it is on, it
 * showed beside the card held over that one.
 */
export function TriageLinks() {
  const svgRef = useRef<SVGSVGElement | null>(null);

  useEffect(() => {
    let frame = 0;
    // A curve each end it leaves from.
    const paths = new Map<HTMLElement, SVGPathElement>();
    const findMiddle = (element: HTMLElement) => {
      const box = element.getBoundingClientRect();
      return { x: box.left + box.width / 2, y: box.top + box.height / 2 };
    };
    // The list it is in: what a line to it is drawn inside.
    const findList = (element: HTMLElement) =>
      element.closest<HTMLElement>(
        "[data-selectable-card-list], [data-link-clip]",
      );
    // Under what is held over its list's rows as they scroll past
    // (``data-link-cover``) - the row the keys are on, held at its top or
    // foot - and so not drawn, nor any line from it. Level, not inside: a
    // ball sits half past its card's edge, beside the card held, and is under
    // it all the same.
    // Each list's held rows, read once a frame - not once a ball, which
    // made a frame long enough to catch rows half way through a move.
    let heldIn = new Map<
      HTMLElement,
      { cover: HTMLElement; top: number; bottom: number }[]
    >();
    const isCovered = (element: HTMLElement, at: { y: number }) => {
      const list = findList(element);
      if (!list) return false;
      let held = heldIn.get(list);
      if (!held) {
        held = [
          ...list.querySelectorAll<HTMLElement>(
            "[data-selectable-row-active], [data-link-cover]",
          ),
        ].map((cover) => {
          const box = cover.getBoundingClientRect();
          return { cover, top: box.top, bottom: box.bottom };
        });
        heldIn.set(list, held);
      }
      return held.some(
        ({ cover, top, bottom }) =>
          !cover.contains(element) && at.y >= top && at.y <= bottom,
      );
    };
    const draw = () => {
      frame = requestAnimationFrame(draw);
      const svg = svgRef.current;
      if (!svg) return;
      heldIn = new Map();
      // Each ball's place read first, every one, then each shown or hidden:
      // a write between two reads lays the page out again for the second.
      const balls = [
        ...document.querySelectorAll<HTMLElement>(
          "[data-link-from], [data-link-to]",
        ),
      ];
      const hidden = balls.map((ball) => isCovered(ball, findMiddle(ball)));
      balls.forEach((ball, at) => {
        if ((ball.style.visibility === "hidden") !== hidden[at]) {
          ball.style.visibility = hidden[at] ? "hidden" : "";
        }
      });
      const drawn = new Set<HTMLElement>();
      for (const from of document.querySelectorAll<HTMLElement>(
        "[data-link-from][data-link-engaged='true']",
      )) {
        const link = from.dataset.linkFrom ?? "";
        const to = document.querySelector<HTMLElement>(
          `[data-link-to="${CSS.escape(link)}"][data-link-engaged='true']`,
        );
        if (!to) continue;
        // Nothing to or from a ball fading out with its list (Similar's, as
        // it closes), nor one no longer laid out - its box is the page's
        // corner, and a line to it shot off there.
        if (
          [from, to].some(
            (ball) =>
              ball.closest("[data-balls-hidden]") != null ||
              ball.getClientRects().length === 0,
          )
        ) {
          continue;
        }
        const start = findMiddle(from);
        const end = findMiddle(to);
        if (isCovered(from, start) || isCovered(to, end)) continue;
        // Leaving a card scrolled out of its list, no line.
        const fromList = findList(from)?.getBoundingClientRect();
        if (fromList && (start.y < fromList.top || start.y > fromList.bottom)) {
          continue;
        }
        // Coming to one scrolled out of its list - the suggestion picked - it
        // runs on up or down to the list's edge, where it went, and stops
        // there: clipped, nothing along the edge.
        const toList = findList(to)?.getBoundingClientRect();
        const clipped =
          toList && (end.y < toList.top || end.y > toList.bottom)
            ? end.y < toList.top
              ? toList.top
              : toList.bottom
            : null;
        let path = paths.get(from);
        if (!path) {
          path = document.createElementNS("http://www.w3.org/2000/svg", "path");
          path.setAttribute("fill", "none");
          path.setAttribute("stroke", "var(--primary)");
          path.setAttribute("stroke-width", "1.5");
          path.dataset.testid = "triage-link-curve";
          path.dataset.link = link;
          svg.appendChild(path);
          paths.set(from, path);
        }
        // In steps: level out of the one, across to half way, up or down to
        // the other's height there, and level into it.
        const across = start.x + (end.x - start.x) / 2;
        path.setAttribute(
          "d",
          clipped != null
            ? `M ${start.x} ${start.y} H ${across} V ${clipped}`
            : `M ${start.x} ${start.y} H ${across} V ${end.y} H ${end.x}`,
        );
        drawn.add(from);
      }
      for (const [from, path] of paths) {
        if (!drawn.has(from)) {
          path.remove();
          paths.delete(from);
        }
      }
    };
    frame = requestAnimationFrame(draw);
    return () => {
      cancelAnimationFrame(frame);
      for (const path of paths.values()) path.remove();
    };
  }, []);

  // On the page itself, over both panels and the divider between them - not
  // under the cards of either - and under any popup or dialog.
  return createPortal(
    <svg
      ref={svgRef}
      aria-hidden
      data-testid="triage-links"
      style={{
        position: "fixed",
        inset: 0,
        width: "100%",
        height: "100%",
        pointerEvents: "none",
        overflow: "visible",
        zIndex: 999,
      }}
    />,
    document.body,
  );
}
